import { useEffect, useMemo, useRef } from "react";
import { AccessibilityInfo, Animated, View } from "react-native";
import type { CustomLevelIconName } from "../../../../icons/custom-level-icon-names";
import Box from "../../../Box";
import { ToeicLevelConnector } from "./ToeicLevelConnector";
import { ToeicLevelRing } from "./ToeicLevelRing";
import {
	CONNECTOR_DRAW_DURATION,
	ILLUSTRATION_ROW_WIDTH,
	RING_DRAW_DURATION,
	WATCHDOG_GRACE_MS,
	buildDrawTimeline,
} from "./constants";

export type ToeicLevelNode = {
	id: string;
	icon: CustomLevelIconName;
	name: string;
	band: string;
};

type Props = {
	levels: ToeicLevelNode[];
	accessibilityLabel: string;
	animate?: boolean;
};

export const ToeicLevelProgression = ({
	levels,
	accessibilityLabel,
	animate = true,
}: Props) => {
	// Values start at the END state and the draw-in rewinds them first. If the
	// animation never runs — reduced motion, a host that does not drive JS-based
	// Animated, an interrupted mount — the illustration is simply already drawn.
	// Starting at 0 instead would make any of those render nothing at all.
	const values = useRef({
		rings: levels.map(() => new Animated.Value(1)),
		connectors: levels.slice(1).map(() => new Animated.Value(1)),
	}).current;

	const timeline = useMemo(
		() => buildDrawTimeline(levels.length),
		[levels.length],
	);

	useEffect(() => {
		if (!animate) {
			return;
		}

		let cancelled = false;
		let running: Animated.CompositeAnimation | undefined;
		let watchdog: ReturnType<typeof setTimeout> | undefined;

		const allValues = [...values.rings, ...values.connectors];
		const setAll = (value: number) => {
			for (const animatedValue of allValues) {
				animatedValue.setValue(value);
			}
		};

		AccessibilityInfo.isReduceMotionEnabled().then((reduceMotion) => {
			// Reduced motion needs no special case — leaving the values alone is
			// already the held end state.
			if (cancelled || reduceMotion) {
				return;
			}

			setAll(0);

			const toTiming = (
				value: Animated.Value,
				segmentStart: number,
				duration: number,
			) =>
				Animated.timing(value, {
					toValue: 1,
					duration,
					delay: segmentStart * timeline.duration,
					useNativeDriver: false,
				});

			running = Animated.parallel([
				...timeline.rings.map((segment, index) =>
					toTiming(values.rings[index], segment.start, RING_DRAW_DURATION),
				),
				...timeline.connectors.map((segment, index) =>
					toTiming(
						values.connectors[index],
						segment.start,
						CONNECTOR_DRAW_DURATION,
					),
				),
			]);

			running.start(({ finished }) => {
				// An interrupted run must not leave the illustration half-drawn.
				if (!(finished || cancelled)) {
					setAll(1);
				}
			});

			// Rewinding to 0 above is only safe if something actually drives the
			// animation back to 1. Not every host does: JS-based Animated needs a
			// running rAF loop, and under react-native-web in Storybook the timings
			// never tick, which would strand the illustration blank forever. The
			// watchdog guarantees the end state either way; on a healthy host the
			// animation has long since finished and this is a no-op.
			watchdog = setTimeout(() => {
				if (!cancelled) {
					setAll(1);
				}
			}, timeline.duration + WATCHDOG_GRACE_MS);
		});

		return () => {
			cancelled = true;
			if (watchdog) {
				clearTimeout(watchdog);
			}
			running?.stop();
			setAll(1);
		};
	}, [animate, values, timeline]);

	return (
		<View
			accessible
			accessibilityRole="image"
			accessibilityLabel={accessibilityLabel}
		>
			<Box
				flexDirection="row"
				alignItems="flex-start"
				justifyContent="center"
				width={ILLUSTRATION_ROW_WIDTH}
			>
				{levels.map((level, index) => {
					const connectorValue = values.connectors[index - 1];
					return (
						<Box key={level.id} flexDirection="row" alignItems="flex-start">
							{connectorValue ? (
								<ToeicLevelConnector progress={connectorValue} />
							) : null}
							<ToeicLevelRing
								icon={level.icon}
								name={level.name}
								band={level.band}
								progress={values.rings[index]}
							/>
						</Box>
					);
				})}
			</Box>
		</View>
	);
};
